import {
  captureEvent,
  extractErrorProperties,
} from "context/posthog/captureEvent";
import { POSTHOG_EVENTS, POSTHOG_FLOWS } from "context/posthog/events";
import { useSomethingWentWrongToast } from "hooks/useSomethingWentWrongToast";
import { isForbiddenError } from "modules/common/utils/isForbiddenError";

import { useTranslatedToasts } from "../../../hooks/toasts/useTranslatedToasts";
import { useApproveRefundMutation } from "../api/Refunds";
import { REFUNDS_TOASTS } from "../constants/Toasts";

import type { UUID } from "utils";

export function useApproveRefund() {
  const [approveRefund, { error, isError, isLoading }] =
    useApproveRefundMutation();
  const { showApproveSuccessToast, showForbiddenToast } = useTranslatedToasts(
    REFUNDS_TOASTS,
    "refunds"
  );

  // A 403 means the backend does not allow this user to approve the refund, so
  // it gets its own message instead of the generic failure one.
  const hasApprovePermission = !isForbiddenError(error);

  useSomethingWentWrongToast(
    isError && hasApprovePermission,
    undefined,
    undefined,
    error
  );

  const handleApproveRefund = async (refundId: UUID, scaToken?: string) => {
    try {
      await approveRefund({ id: refundId, scaToken }).unwrap();

      captureEvent(POSTHOG_EVENTS.approveRefundSucceeded, {
        flow: POSTHOG_FLOWS.approveRefund,
        entityId: refundId,
      });

      showApproveSuccessToast();
    } catch (approveError) {
      // Not rethrown - it would end up as an unhandled rejection in the click
      // handler (and in the SCA modals, which do not catch it either) and be
      // reported as a raw `{ status, data }` object.
      captureEvent(POSTHOG_EVENTS.approveRefundFailed, {
        flow: POSTHOG_FLOWS.approveRefund,
        entityId: refundId,
        ...extractErrorProperties(approveError),
      });

      if (isForbiddenError(approveError)) {
        showForbiddenToast();
      }
    }
  };

  return {
    approveRefund: handleApproveRefund,
    isApproveRefundLoading: isLoading,
  };
}
